import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Button,
  CitationChip,
  GlassCard,
  Pill,
  SurfacePageSectionHeader,
} from '@/design-system';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { DrawerProps } from '@/app/providers';
import { usePersona } from '@/app/providers';
import { ControlChart } from '@/versions/v1/modules/_viz';
import { SeverityChip } from '@/versions/v1/modules/advisory/SeverityChip';
import {
  AnalysisRunFacts,
  AnalysisRunFinancial,
  AnalysisRunKnowledge,
  AnalysisRunMode,
  AnalysisRunPropagation,
  AnalysisRunProvenance,
  AnalysisRunQuality,
  AnalysisRunSignal,
} from '@/versions/v1/modules/advisory/AnalysisRun';
import { AnalysisRunDiagnosis } from '@/versions/v1/modules/advisory/AnalysisRunDiagnosis';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { isCasePinnedForReport } from '@/versions/v1/shared/AdvisoryEngine';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useAnalysisRun } from '@/versions/v1/shared/useAnalysisRun';
import { useAnalysisReplay } from '@/versions/v1/shared/useAnalysisReplay';
import { AgentInvestigationSection } from '@/versions/v1/modules/advisory/AgentInvestigation';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { formatCaseClock, formatMonthDay } from '@/versions/v1/shared/clock.derive';
import {
  assigneeLabel,
  drawerActionGroups,
  ASSIGNEE_SHORT,
  BRANCH_CHIP,
  CAUSE_CATEGORY_LABEL,
  caseById,
  casesWithSession,
  matchedRulesForCase,
  outcomeForCase,
  recommendedActionsWithSession,
  respondByLabel,
  reviewActorLabel,
  fmeaReviewsWithSession,
  reviewsForCase,
  ruleIsLive,
  severityChip,
  STATE_CHIP,
  URGENCY_LABEL,
  urgencyTone,
  variationEventForCase,
} from '@/versions/v1/modules/advisory/Scene.transform';
import {
  actionDecisionLine,
  drawerLadderSection,
  drawerLadderTierLines,
  drawerRivalLine,
  drawerRuleReviewAdmin,
  effectivenessLineForAction,
  learnedProvenanceForCase,
  matchedRuleCauseHref,
  matchedRuleConditionDetail,
  matchedRuleDiagnosis,
  matchedRuleOutcomeLabel,
  samplePointNodeHref,
  relatedCaseLine,
  signalSectionForCase,
  variationDescriptionForCase,
  variationMagnitudeForCase,
} from '@/versions/v1/modules/advisory/CaseDrawer.transform';
import { constraintLineForAction } from '@/versions/v1/shared/constraint.transform';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { ruleOutcome, RULE_OUTCOME_LABEL } from '@/versions/v1/shared/RuleSetEngine';

type CaseDrawerData = {
  caseId?: string;
  /** ma.D1 / #120 fix round 1 (MR-F1): the checkpoint-measurement payload
   *  variant of this drawer kind — a mine/mill/shipped reconciliation
   *  checkpoint has mass, grade, contained metal, a source string and a
   *  provenance string, but it is not an Advisory case (no diagnosed cause,
   *  no branch, no state) — reusing `caseId` would either invent a case that
   *  does not exist or point every checkpoint at one real case that only
   *  describes one of them. Smallest new payload on the existing drawer kind,
   *  rather than a new kind, per the build-plan's drawer-registry guidance. */
  measurement?: ChainCheckpointMeasurement;
};

export type ChainCheckpointMeasurement = {
  checkpointLabel: string;
  element: string;
  mass: string;
  grade: string;
  contained: string;
  source: string;
  provenance: string;
  /** Whether this figure has no Bunker Hill source (provenance starts
   *  `[syn]`) — gates the Provenance section's traceability claim so a
   *  synthetic figure is never told to a reader as traceable (MR-F1). */
  synthetic: boolean;
};

function trailStamp(at: string | undefined, cursor: string): string {
  if (!at) return '—';
  const day = formatMonthDay(at, cursor);
  if (at.includes('T')) {
    const time = at.split('T')[1]?.slice(0, 5) ?? '';
    return `${day} · ${time}`;
  }
  if (/^\d{2}:\d{2}$/.test(at)) return `${day} · ${at}`;
  return day;
}

export function CaseDrawer({ props }: { props: DrawerProps }) {
  const data = (props.data ?? {}) as CaseDrawerData;
  const caseId = data.caseId;
  const measurement = data.measurement;
  const { projectId: slug = 'alpha' } = useParams<{ projectId: string }>();
  const { state } = useScenario();
  const advisory = useAdvisory();
  const { can } = useRole();
  const { persona } = usePersona();
  const [rejectFormFor, setRejectFormFor] = useState<string | null>(null);
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});
  const [historyOpen, setHistoryOpen] = useState(false);

  // Read above the early returns: `Drawer.tsx` renders this body without a
  // key, so a hook below one would change the hook count the moment a case
  // drawer replaces a measurement drawer.
  const cases = casesWithSession(state, advisory);
  const caseItem = caseId ? caseById(cases, caseId) : undefined;
  const run = useAnalysisRun(caseItem?.analysisRun?.fixture);
  const replay = useAnalysisReplay(caseItem?.analysisRun?.fixture);
  const displayRun =
    run.status === 'ready' && replay.status === 'ready'
      ? { ...run, result: replay.frame.result }
      : run;

  if (measurement) {
    return <MeasurementBody measurement={measurement} />;
  }

  // allActionsForCase (not the sequence-filtered actionsForCase) so a
  // zero-touch action (sequence: null, e.g. ra-zn-ph-1 on the CRO's case)
  // still renders its decision record here — the merged numbered list on
  // Scene.tsx is a different, narrower view (route-reads paul-feedback.A8).
  const actionGroups = caseItem
    ? drawerActionGroups(recommendedActionsWithSession(advisory), caseItem.id)
    : { pending: [], history: [] };
  const diagnosis = diagnose(state, advisory);
  const variation = caseItem ? variationEventForCase(caseItem, state) : undefined;
  const matchedRules = caseItem ? matchedRulesForCase(caseItem, state, advisory) : [];
  const outcome = caseItem ? outcomeForCase(caseItem.id) : undefined;
  const reviews = caseItem ? reviewsForCase(caseItem.id, advisory) : [];
  const rivalLine = caseItem ? drawerRivalLine(caseItem, diagnosis) : undefined;
  const learnedProvenance = caseItem ? learnedProvenanceForCase(caseItem, advisory) : undefined;
  const ruleReview = caseItem
    ? drawerRuleReviewAdmin(caseItem, fmeaReviewsWithSession(advisory), slug)
    : undefined;
  const pinnedForReport = caseItem ? isCasePinnedForReport(advisory, caseItem.id) : false;

  if (!caseItem) {
    return (
      <div className="flex-1 p-5 overflow-y-auto scroll-soft">
        <p className="text-sm text-muted">Case not found.</p>
      </div>
    );
  }

  const stateChip = STATE_CHIP[caseItem.state];
  const branchChip = BRANCH_CHIP[caseItem.branch];
  const respondLabel = respondByLabel(caseItem, state.cursor);
  const ladderSection = drawerLadderSection(caseItem, state.cursor);
  const currentTier = ladderSection?.tiers.find((tier) => tier.status === 'current');
  const signalSection = signalSectionForCase(caseItem, state, diagnosis);
  const outcomeLabel = matchedRuleOutcomeLabel(caseItem, diagnosis);
  return (
    <div className="flex-1 overflow-y-auto scroll-soft">
      <section className="space-y-2 border-b border-[var(--hairline)] px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <SeverityChip label={severityChip(caseItem).label} tone={severityChip(caseItem).tone} />
          <span className="text-sm text-muted">{caseItem.circuit}</span>
          <Pill tone={stateChip.tone}>{stateChip.label}</Pill>
          <Pill tone={branchChip.tone}>{branchChip.label}</Pill>
          {caseItem.handler ? (
            <Pill tone="default">
              {caseItem.handler.name} · {assigneeLabel(caseItem.handler.role)} ·{' '}
              {formatCaseClock(caseItem.handler.since, state.cursor)}
            </Pill>
          ) : null}
          {currentTier ? <Pill tone="warning">Tier {currentTier.tier}</Pill> : null}
        </div>
        {respondLabel ? <p className="text-sm text-muted">{respondLabel}</p> : null}
        {caseItem.analysisRun ? <AnalysisRunFacts run={displayRun} /> : null}
        {persona === 'GM' ? (
          <Button
            type="button"
            variant="ghost"
            className="h-8 px-2 text-xs"
            onClick={() =>
              pinnedForReport
                ? advisory.unpinCaseFromReport(caseItem.id)
                : advisory.pinCaseForReport(caseItem.id)
            }
          >
            {pinnedForReport ? 'Untrack from daily report' : 'Track in daily report'}
          </Button>
        ) : null}
      </section>

      {caseItem.analysisRun ? (
        <>
          <AnalysisRunQuality run={displayRun} />
          <AnalysisRunMode run={displayRun} />
          <AnalysisRunSignal run={displayRun} replay={replay} />
          <AnalysisRunDiagnosis run={displayRun} />
          <AnalysisRunPropagation run={displayRun} />
          <AnalysisRunFinancial run={displayRun} />
          <AnalysisRunKnowledge run={displayRun} />
        </>
      ) : null}

      {actionGroups.pending.length > 0 || actionGroups.history.length > 0 ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Recommended actions" />
          {actionGroups.pending.length > 0 ? (
            <ul className="space-y-3">
              {actionGroups.pending.map((action, index) => {
                const decisionLine = actionDecisionLine(action);
                const showRejectForm = rejectFormFor === action.id;
                return (
                  <li
                    key={action.id}
                    className="flex flex-wrap items-start justify-between gap-3 text-sm"
                  >
                    <div>
                      <p>
                        <span className="tabular text-muted">{index + 1}</span>{' '}
                        <SeverityChip
                          label={URGENCY_LABEL[action.urgency]}
                          tone={urgencyTone(action.urgency)}
                          solid
                          className="mx-1"
                        />
                        {action.text}
                      </p>
                      <p className="mt-1 text-xs text-muted">{decisionLine.statusLabel}</p>
                      {action.constraint ? (
                        (() => {
                          const constraintLine = constraintLineForAction(action, caseItem, state);
                          return constraintLine ? (
                            <p className="mt-1 text-xs text-muted">{constraintLine.text}</p>
                          ) : null;
                        })()
                      ) : null}
                    </div>
                    {can('action:approve') && action.status === 'proposed' ? (
                      <div className="flex w-full flex-wrap items-start justify-end gap-2 sm:w-auto">
                        <Button variant="primary" onClick={() => advisory.approveAction(action.id)}>
                          Approve action
                        </Button>
                        {showRejectForm ? (
                          <div className="flex w-full flex-col gap-2">
                            <textarea
                              aria-label={`Reason for refusing "${action.text}"`}
                              className="min-h-[4rem] w-full rounded-md border border-[var(--hairline)] bg-transparent px-3 py-2 text-sm"
                              placeholder="Reason for refusal"
                              value={rejectReasons[action.id] ?? ''}
                              onChange={(event) =>
                                setRejectReasons((current) => ({
                                  ...current,
                                  [action.id]: event.target.value,
                                }))
                              }
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                disabled={!(rejectReasons[action.id] ?? '').trim()}
                                onClick={() => {
                                  advisory.rejectAction(action.id, rejectReasons[action.id] ?? '');
                                  setRejectFormFor(null);
                                }}
                              >
                                Confirm refusal
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setRejectFormFor(null);
                                  setRejectReasons((current) => {
                                    const next = { ...current };
                                    delete next[action.id];
                                    return next;
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button variant="ghost" onClick={() => setRejectFormFor(action.id)}>
                            Refuse
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {actionGroups.history.length > 0 ? (
            <div className="mt-3">
              <button
                type="button"
                className="text-sm font-medium text-accent-700 dark:text-accent-400"
                onClick={() => setHistoryOpen((open) => !open)}
              >
                History · {actionGroups.history.filter((a) => a.status === 'done').length} implemented
                · {actionGroups.history.filter((a) => a.status === 'rejected').length} refused
              </button>
              {historyOpen ? (
                <ul className="mt-2 space-y-3">
                  {actionGroups.history.map((action) => {
                    const effectiveness = effectivenessLineForAction(
                      caseItem,
                      action,
                      state,
                      advisory,
                      diagnosis,
                      recommendedActionsWithSession(advisory),
                    );
                    const decisionLine = actionDecisionLine(action);
                    return (
                      <li key={action.id} className="text-sm text-muted">
                        <p className="text-zinc-900 dark:text-zinc-100">{action.text}</p>
                        <p className="mt-1 text-xs">
                          {decisionLine.statusLabel === 'implemented' && decisionLine.implementedAt
                            ? `implemented ${decisionLine.implementedAt}`
                            : decisionLine.statusLabel}
                          {decisionLine.decisionDetail ? ` · decided ${decisionLine.decisionDetail}` : ''}
                        </p>
                        {decisionLine.reason ? (
                          <p className="mt-1 text-xs">
                            <span className="font-medium">Reason:</span> {decisionLine.reason}
                          </p>
                        ) : null}
                        {effectiveness ? (
                          <p className="mt-1 text-xs">
                            Effectiveness · {effectiveness.state} · {effectiveness.signalLabel}
                            {effectiveness.successDirection ? ` · ${effectiveness.successDirection}` : ''} ·{' '}
                            {effectiveness.observedTrend} since {effectiveness.since}
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {variation ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Variation event" />
          <div className="text-sm">
            <p className="font-medium text-zinc-900 dark:text-zinc-100">
              {variationDescriptionForCase(caseItem, variation.description, state)}
            </p>
            {relatedCaseLine(caseItem) ? (
              <p className="mt-1 text-xs text-muted">{relatedCaseLine(caseItem)}</p>
            ) : null}
            {outcomeLabel ? (
              <p className="mt-1 text-xs text-muted">{outcomeLabel}</p>
            ) : null}
            <p className="mt-1 text-muted">
              {variationMagnitudeForCase(caseItem, variation.magnitude, state)}
            </p>
            <p className="mt-1 text-xs text-muted">
              detected {formatCaseClock(variation.detectedAt, state.cursor)} · subprocess{' '}
              {variation.subprocessId.replace('sp-', '')}
            </p>
          </div>
        </section>
      ) : null}

      {signalSection?.kind === 'honest' ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Signal" />
          <p className="text-sm text-muted">{signalSection.message}</p>
        </section>
      ) : signalSection?.kind === 'chart' ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Signal" />
          <ControlChart model={signalSection.chart} />
        </section>
      ) : null}

      {matchedRules.length > 0 ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Matched rules" />
          <ul className="space-y-2 text-sm">
            {matchedRules.map((rule) => {
              const diagnosisLine = matchedRuleDiagnosis(rule.ruleId);
              const rowRule = ruleById(rule.ruleId);
              const rowOutcome = rowRule ? ruleOutcome(rowRule) : undefined;
              const conditionDetail = matchedRuleConditionDetail(rule.ruleId, caseItem);
              const causeHref = matchedRuleCauseHref(slug, rule.ruleId);
              return (
                <li key={rule.ruleId} className="space-y-1 text-muted">
                  {diagnosisLine ? (
                    <p className="font-medium text-zinc-900 dark:text-zinc-100">{diagnosisLine}</p>
                  ) : null}
                  {conditionDetail ? <p className="text-xs">{conditionDetail}</p> : null}
                  <p className="text-xs">
                    {causeHref ? (
                      <Link
                        to={causeHref}
                        className="font-mono text-accent-700 dark:text-accent-400"
                      >
                        {rule.ruleId}
                      </Link>
                    ) : (
                      <span className="font-mono">{rule.ruleId}</span>
                    )}
                    {rowOutcome ? (
                      <span className="ml-2">{RULE_OUTCOME_LABEL[rowOutcome]}</span>
                    ) : null}
                    {ruleIsLive(rule.ruleId, advisory) ? (
                      <Pill tone="success" className="ml-2">
                        live
                      </Pill>
                    ) : null}
                  </p>
                </li>
              );
            })}
          </ul>
          {rivalLine ? (
            <p className="text-sm text-muted">
              Rival · {rivalLine.label} · {rivalLine.pct} %
            </p>
          ) : null}
        </section>
      ) : null}

      {caseItem.controlModeCheck ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Control mode" subtitle="Checked before any recommendation" />
          <p className="text-sm text-muted">
            {caseItem.controlModeCheck.tag} = {caseItem.controlModeCheck.mode.toUpperCase()}
            {caseItem.controlModeCheck.confirmedBy ? (
              <span>
                , confirmed by {ASSIGNEE_SHORT[caseItem.controlModeCheck.confirmedBy]}{' '}
                {caseItem.controlModeCheck.confirmedAt}
              </span>
            ) : null}
          </p>
        </section>
      ) : null}

      {caseItem.evidenceRequests.length > 0 ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Evidence requests" />
          <ul className="space-y-2 text-sm">
            {caseItem.evidenceRequests.map((request) => (
              <li key={request.id} className="flex gap-2 text-muted">
                <span aria-hidden>{request.status === 'answered' ? '[x]' : '[~]'}</span>
                <span>
                  {request.text} → {ASSIGNEE_SHORT[request.target]}
                  {caseItem.samplingArtefactCheck && request.kind === 'lab-sample'
                    ? ` · lab due ${caseItem.samplingArtefactCheck.labDueAt}`
                    : ''}
                  {request.answer ? `, answered ${request.answer}` : ''}
                </span>
              </li>
            ))}
            {caseItem.confidenceDiscount?.samplePointId ? (
              <li className="flex gap-2 text-muted">
                <span aria-hidden>[~]</span>
                <span>
                  <Link
                    to={samplePointNodeHref(slug, caseItem.confidenceDiscount.samplePointId)}
                    className="font-medium text-accent-700 dark:text-accent-400"
                  >
                    {caseItem.confidenceDiscount.samplePointId}
                  </Link>
                  {' · '}
                  {caseItem.confidenceDiscount.reason}
                </span>
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}

      {ladderSection ? (
        <section
          id="walkthrough-escalation-ladder"
          className="space-y-3 border-b border-[var(--hairline)] px-5 py-4"
        >
          <SurfacePageSectionHeader title="Escalation ladder" />
          <ul className="space-y-2 text-sm">
            {drawerLadderTierLines(ladderSection.tiers).map((line) => (
              <li key={line} className="text-muted">{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {outcome ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader
            title="Outcome"
            subtitle="proposed, not live"
          />
          <dl className="space-y-2 text-sm text-muted">
            <div>
              <dt className="text-xs uppercase tracking-wide">diagnosed cause</dt>
              <dd className="text-zinc-900 dark:text-zinc-100">{outcome.diagnosedCause}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide">cause category</dt>
              <dd>{CAUSE_CATEGORY_LABEL[outcome.causeCategory] ?? outcome.causeCategory}</dd>
            </div>
            {ruleReview ? (
              <div>
                <dt className="text-xs uppercase tracking-wide">rule review</dt>
                <dd>
                  <Link
                    to={ruleReview.href}
                    className="text-accent-700 hover:underline dark:text-accent-400"
                  >
                    rule proposed · {ruleReview.reviewState}
                  </Link>
                </dd>
              </div>
            ) : null}
          </dl>
        </section>
      ) : null}

      {reviews.length > 0 ? (
        <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
          <SurfacePageSectionHeader title="Review & approval trail" />
          <ol className="relative space-y-4 border-l border-[var(--hairline-strong)] pl-4 text-sm">
            {reviews.flatMap((review) => {
              const events: { key: string; label: string; detail?: string; reason?: string }[] =
                [];
              if (review.reviewedBy) {
                events.push({
                  key: `${review.id}-review`,
                  label: reviewActorLabel(review.reviewedBy, review.reviewSeats),
                  detail: `reviewed ${trailStamp(review.reviewedAt, state.cursor)}`,
                });
              } else if (review.reviewStatus === 'pending') {
                events.push({
                  key: `${review.id}-review-pending`,
                  label: reviewActorLabel(undefined, review.reviewSeats),
                  detail: 'awaiting review',
                });
              }
              if (review.approvalStatus === 'approved' && review.approvedBy) {
                events.push({
                  key: `${review.id}-approve`,
                  label: 'Plant Manager',
                  detail: `approved ${trailStamp(review.approvedAt, state.cursor)}`,
                });
                if (review.proposedChange.ruleId && ruleIsLive(review.proposedChange.ruleId, advisory)) {
                  events.push({
                    key: `${review.id}-live`,
                    label: 'Rule live',
                    detail: `${review.proposedChange.ruleId} is now live in the FMEA`,
                  });
                }
              } else if (review.approvalStatus === 'pending') {
                events.push({
                  key: `${review.id}-approve-pending`,
                  label: 'Plant Manager',
                  detail: `awaiting sign-off → ${review.id}`,
                });
              }
              if (review.reviewStatus === 'rejected') {
                events.push({
                  key: `${review.id}-reject`,
                  label: 'Review rejected',
                  reason: review.reviewNotes,
                });
              }
              return events;
            }).map((event) => (
              <li key={event.key} className="relative">
                <span
                  className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-[var(--accent)]"
                  aria-hidden
                />
                <p className="font-medium text-zinc-900 dark:text-zinc-100">{event.label}</p>
                {event.detail ? <p className="text-muted">{event.detail}</p> : null}
                {event.reason ? (
                  <p className="text-muted">
                    <span className="font-medium">Reason:</span> {event.reason}
                  </p>
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {learnedProvenance ? (
        <section className="space-y-3 px-5 py-4">
          <SurfacePageSectionHeader title="Provenance" />
          <GlassCard className="px-4 py-3 text-sm text-muted">
            <p className="font-medium text-zinc-900 dark:text-zinc-100">{learnedProvenance}</p>
            <div className="mt-2">
              <CitationChip confidence="medium" label="provenance" />
            </div>
          </GlassCard>
        </section>
      ) : null}
      <AnalysisRunProvenance run={displayRun} />
      {caseItem.analysisRun ? (
        <AgentInvestigationSection
          caseId={caseItem.id}
          fixture={caseItem.analysisRun.fixture}
          diagnosedAt={caseItem.diagnosedAt as SeriesTime | undefined}
        />
      ) : null}
    </div>
  );
}


/** ma.D1 / #120 fix round 1 (MR-F1) — the checkpoint-measurement body. Mass,
 *  grade, contained metal, source string and provenance, per Q7. The
 *  Provenance section never tells a synthetic figure it is traceable to a
 *  source — that claim is reserved for the mill checkpoint, which is
 *  genuinely computed from the existing Module 2 seed. */
function MeasurementBody({ measurement }: { measurement: ChainCheckpointMeasurement }) {
  const cells = [
    { label: 'Mass', value: measurement.mass },
    { label: 'Grade', value: measurement.grade },
    { label: 'Contained metal', value: measurement.contained },
  ];

  return (
    <div className="flex-1 overflow-y-auto scroll-soft">
      <section className="space-y-3 border-b border-[var(--hairline)] px-5 py-4">
        <div className="grid grid-cols-3 gap-3">
          {cells.map((cell) => (
            <div
              key={cell.label}
              className="rounded-xl border border-[var(--glass-border)] bg-[var(--glass-surface-soft)] px-3 py-2"
            >
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted">{cell.label}</p>
              <p className="mt-1 text-sm font-semibold tabular tracking-tight text-zinc-900 dark:text-zinc-100">
                {cell.value}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-2 border-b border-[var(--hairline)] px-5 py-4">
        <SurfacePageSectionHeader title="Source" />
        <p className="text-sm text-muted">{measurement.source}</p>
      </section>

      <section className="space-y-3 px-5 py-4">
        <SurfacePageSectionHeader title="Provenance" />
        <GlassCard className="px-4 py-3 text-sm text-muted">
          <p>
            {measurement.synthetic
              ? 'No site source — design-basis stand-in.'
              : 'Computed from the site measurement and reconciliation seed.'}
          </p>
          <div className="mt-2">
            <ProvenanceChip text={measurement.provenance} full />
          </div>
        </GlassCard>
      </section>
    </div>
  );
}
